import React, { useState, useEffect, useRef, useContext } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment } from '@react-three/drei';
import * as THREE from 'three';
import axios from 'axios';
import './MeshViewer.css';
import { getOrganSegEC2Endpoint, APIFileVisualization } from '../../helper/APIFunctions';
import { loadDemoMesh } from '../../helper/DemoDataLoader';
import ShareModal from '../VisualModals/Share';
import { VisualCache } from '../../helper/Context';

function OrganMesh({ vertices, faces, normals, color, opacity, name, onHover, onHoverEnd, isHovered }) {
  const meshRef = useRef();

  useEffect(() => {
    if (meshRef.current && vertices && faces) {
      const geometry = new THREE.BufferGeometry();
      const verticesArray = new Float32Array(vertices.flat());
      geometry.setAttribute('position', new THREE.BufferAttribute(verticesArray, 3));
      const indicesArray = new Uint32Array(faces.flat());
      geometry.setIndex(new THREE.BufferAttribute(indicesArray, 1));
      if (normals && normals.length === vertices.length) {
        const normalsArray = new Float32Array(normals.flat());
        geometry.setAttribute('normal', new THREE.BufferAttribute(normalsArray, 3));
      } else {
        geometry.computeVertexNormals();
      }
      meshRef.current.geometry = geometry;
    }
  }, [vertices, faces, normals]);

  return (
    <mesh 
      ref={meshRef} 
      castShadow 
      receiveShadow
      onPointerOver={(e) => {
        e.stopPropagation();
        if (onHover) onHover(name);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        if (onHoverEnd) onHoverEnd();
      }}
    >
      <meshStandardMaterial
        color={color}
        opacity={opacity}
        transparent={opacity < 1}
        side={THREE.DoubleSide}
        roughness={0.3}
        metalness={0.1}
        emissive={isHovered ? color : '#000000'}
        emissiveIntensity={isHovered ? 0.3 : 0}
      />
    </mesh>
  );
}

function MeshViewer({ organ, isDemo = false, fileType = null, viewerLayout = null, onToggleLayout = null }) {
  const [meshData, setMeshData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleOrgans, setVisibleOrgans] = useState({});
  const [showDownload, setShowDownload] = useState(false);
  const [showOrgans, setShowOrgans] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [hoveredOrgan, setHoveredOrgan] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [createdVisualization, setCreatedVisualization] = useState(null);
  const [uploadingShare, setUploadingShare] = useState(false);
  const { visCache, setVisCache } = useContext(VisualCache);

  // Check if the uploaded file is DICOM format
  const isDicomFile = fileType && (
    fileType.toLowerCase().includes('dicom') || 
    fileType.toLowerCase().includes('.dcm') ||
    fileType.toLowerCase().includes('dcm')
  );

  useEffect(() => {
    loadMesh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organ, isDemo]);

  const loadMesh = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      
      if (isDemo) {
        // Load from S3 demo data (multi-organ mesh with 15 organs)
        data = await loadDemoMesh();
      } else {
        // Load from EC2 backend (existing behavior)
        const ec2Endpoint = getOrganSegEC2Endpoint();
        const response = await axios.get(`${ec2Endpoint}/api/mesh/${organ}`);
        data = response.data;
      }
      
      setMeshData(data);
      if (data.multi_organ) {
        const initialVisible = {};
        data.organs.forEach(organ => {
          initialVisible[organ.name] = true;
        });
        setVisibleOrgans(initialVisible);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load mesh');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrganVisibility = (organName) => {
    setVisibleOrgans(prev => ({
      ...prev,
      [organName]: !prev[organName]
    }));
  };

  const downloadMeshAsOBJ = (organData, organName) => {
    // Convert mesh data to OBJ format
    let objContent = `# OBJ file for ${organName}\n`;
    objContent += `# Vertices: ${organData.vertices.length}\n`;
    objContent += `# Faces: ${organData.faces.length}\n\n`;
    
    // Write vertices
    organData.vertices.forEach(v => {
      objContent += `v ${v[0]} ${v[1]} ${v[2]}\n`;
    });
    
    // Write normals if available
    if (organData.normals && organData.normals.length > 0) {
      objContent += '\n';
      organData.normals.forEach(n => {
        objContent += `vn ${n[0]} ${n[1]} ${n[2]}\n`;
      });
    }
    
    // Write faces (OBJ uses 1-based indexing)
    objContent += '\n';
    organData.faces.forEach(f => {
      if (organData.normals && organData.normals.length > 0) {
        objContent += `f ${f[0]+1}//${f[0]+1} ${f[1]+1}//${f[1]+1} ${f[2]+1}//${f[2]+1}\n`;
      } else {
        objContent += `f ${f[0]+1} ${f[1]+1} ${f[2]+1}\n`;
      }
    });
    
    // Create and download file
    const blob = new Blob([objContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${organName.replace(/\s+/g, '_')}.obj`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadMeshAsSTL = (organData, organName) => {
    // Convert mesh data to ASCII STL format
    let stlContent = `solid ${organName}\n`;
    
    organData.faces.forEach(face => {
      const v1 = organData.vertices[face[0]];
      const v2 = organData.vertices[face[1]];
      const v3 = organData.vertices[face[2]];
      
      // Calculate normal if not provided
      let normal;
      if (organData.normals && organData.normals[face[0]]) {
        normal = organData.normals[face[0]];
      } else {
        // Calculate face normal
        const edge1 = [v2[0] - v1[0], v2[1] - v1[1], v2[2] - v1[2]];
        const edge2 = [v3[0] - v1[0], v3[1] - v1[1], v3[2] - v1[2]];
        normal = [
          edge1[1] * edge2[2] - edge1[2] * edge2[1],
          edge1[2] * edge2[0] - edge1[0] * edge2[2],
          edge1[0] * edge2[1] - edge1[1] * edge2[0]
        ];
        const length = Math.sqrt(normal[0]**2 + normal[1]**2 + normal[2]**2);
        normal = [normal[0]/length, normal[1]/length, normal[2]/length];
      }
      
      stlContent += `  facet normal ${normal[0]} ${normal[1]} ${normal[2]}\n`;
      stlContent += `    outer loop\n`;
      stlContent += `      vertex ${v1[0]} ${v1[1]} ${v1[2]}\n`;
      stlContent += `      vertex ${v2[0]} ${v2[1]} ${v2[2]}\n`;
      stlContent += `      vertex ${v3[0]} ${v3[1]} ${v3[2]}\n`;
      stlContent += `    endloop\n`;
      stlContent += `  endfacet\n`;
    });
    
    stlContent += `endsolid ${organName}\n`;
    
    // Create and download file
    const blob = new Blob([stlContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${organName.replace(/\s+/g, '_')}.stl`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadMeshAsGLB = (organData, organName) => {
    // Convert mesh data to glTF 2.0 binary format (.glb)
    const vertices = new Float32Array(organData.vertices.flat());
    const indices = new Uint32Array(organData.faces.flat());
    const normals = organData.normals ? new Float32Array(organData.normals.flat()) : null;
    
    // Calculate bounding box
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < vertices.length; i += 3) {
      minX = Math.min(minX, vertices[i]);
      minY = Math.min(minY, vertices[i + 1]);
      minZ = Math.min(minZ, vertices[i + 2]);
      maxX = Math.max(maxX, vertices[i]);
      maxY = Math.max(maxY, vertices[i + 1]);
      maxZ = Math.max(maxZ, vertices[i + 2]);
    }
    
    // Create glTF JSON structure
    const gltf = {
      asset: { version: "2.0", generator: "Snorkle Organ Segmentation" },
      scene: 0,
      scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0 }],
      meshes: [{
        primitives: [{
          attributes: {
            POSITION: 0,
            ...(normals ? { NORMAL: 1 } : {})
          },
          indices: normals ? 2 : 1,
          mode: 4 // TRIANGLES
        }]
      }],
      accessors: [
        {
          bufferView: 0,
          componentType: 5126, // FLOAT
          count: vertices.length / 3,
          type: "VEC3",
          min: [minX, minY, minZ],
          max: [maxX, maxY, maxZ]
        },
        ...(normals ? [{
          bufferView: 1,
          componentType: 5126, // FLOAT
          count: normals.length / 3,
          type: "VEC3"
        }] : []),
        {
          bufferView: normals ? 2 : 1,
          componentType: 5125, // UNSIGNED_INT
          count: indices.length,
          type: "SCALAR"
        }
      ],
      bufferViews: [
        {
          buffer: 0,
          byteOffset: 0,
          byteLength: vertices.byteLength,
          target: 34962 // ARRAY_BUFFER
        },
        ...(normals ? [{
          buffer: 0,
          byteOffset: vertices.byteLength,
          byteLength: normals.byteLength,
          target: 34962 // ARRAY_BUFFER
        }] : []),
        {
          buffer: 0,
          byteOffset: normals ? vertices.byteLength + normals.byteLength : vertices.byteLength,
          byteLength: indices.byteLength,
          target: 34963 // ELEMENT_ARRAY_BUFFER
        }
      ],
      buffers: [{
        byteLength: vertices.byteLength + (normals ? normals.byteLength : 0) + indices.byteLength
      }]
    };
    
    // Convert JSON to binary
    const jsonString = JSON.stringify(gltf);
    const jsonBuffer = new TextEncoder().encode(jsonString);
    const jsonPadding = (4 - (jsonBuffer.length % 4)) % 4;
    const jsonChunkLength = jsonBuffer.length + jsonPadding;
    
    // Create binary buffer
    const binaryData = normals 
      ? new Uint8Array([...new Uint8Array(vertices.buffer), ...new Uint8Array(normals.buffer), ...new Uint8Array(indices.buffer)])
      : new Uint8Array([...new Uint8Array(vertices.buffer), ...new Uint8Array(indices.buffer)]);
    const binaryPadding = (4 - (binaryData.length % 4)) % 4;
    const binaryChunkLength = binaryData.length + binaryPadding;
    
    // Create GLB file
    const glbLength = 12 + 8 + jsonChunkLength + 8 + binaryChunkLength;
    const glb = new ArrayBuffer(glbLength);
    const view = new DataView(glb);
    
    // GLB header
    view.setUint32(0, 0x46546C67, true); // magic "glTF"
    view.setUint32(4, 2, true); // version
    view.setUint32(8, glbLength, true); // length
    
    // JSON chunk
    view.setUint32(12, jsonChunkLength, true);
    view.setUint32(16, 0x4E4F534A, true); // "JSON"
    const glbArray = new Uint8Array(glb);
    glbArray.set(jsonBuffer, 20);
    for (let i = 0; i < jsonPadding; i++) {
      glbArray[20 + jsonBuffer.length + i] = 0x20; // space padding
    }
    
    // Binary chunk
    const binaryChunkOffset = 20 + jsonChunkLength;
    view.setUint32(binaryChunkOffset, binaryChunkLength, true);
    view.setUint32(binaryChunkOffset + 4, 0x004E4942, true); // "BIN\0"
    glbArray.set(binaryData, binaryChunkOffset + 8);
    for (let i = 0; i < binaryPadding; i++) {
      glbArray[binaryChunkOffset + 8 + binaryData.length + i] = 0; // zero padding
    }
    
    // Download file
    const blob = new Blob([glb], { type: 'model/gltf-binary' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${organName.replace(/\s+/g, '_')}.glb`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = (format = 'obj') => {
    if (!meshData) return;
    
    if (meshData.multi_organ) {
      // Download all visible organs
      meshData.organs.forEach(organData => {
        if (visibleOrgans[organData.name]) {
          if (format === 'obj') {
            downloadMeshAsOBJ(organData, organData.name);
          } else if (format === 'stl') {
            downloadMeshAsSTL(organData, organData.name);
          } else if (format === 'glb') {
            downloadMeshAsGLB(organData, organData.name);
          }
        }
      });
    } else {
      // Download single organ
      const organData = {
        vertices: meshData.vertices,
        faces: meshData.faces,
        normals: meshData.normals
      };
      if (format === 'obj') {
        downloadMeshAsOBJ(organData, organ);
      } else if (format === 'stl') {
        downloadMeshAsSTL(organData, organ);
      } else if (format === 'glb') {
        downloadMeshAsGLB(organData, organ);
      }
    }
  };

  const createGLBBlob = (organDataArray, sceneName = 'organ_scene') => {
    // Helper function to convert hex color to RGB array [0-1]
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? [
        parseInt(result[1], 16) / 255,
        parseInt(result[2], 16) / 255,
        parseInt(result[3], 16) / 255,
        1.0 // alpha
      ] : [1, 0, 0, 1]; // default to red if parsing fails
    };
    
    // Combine all organs into separate mesh primitives (not combined)
    // This allows each organ to have its own material/color
    const nodes = [];
    const meshes = [];
    const materials = [];
    const accessors = [];
    const bufferViews = [];
    let bufferOffset = 0;
    const binaryDataChunks = [];
    
    organDataArray.forEach((organData, meshIndex) => {
      const vertices = new Float32Array(organData.vertices.flat());
      const indices = new Uint32Array(organData.faces.flat());
      const normals = organData.normals ? new Float32Array(organData.normals.flat()) : null;
      
      // Calculate bounding box for this mesh
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      for (let i = 0; i < vertices.length; i += 3) {
        minX = Math.min(minX, vertices[i]);
        minY = Math.min(minY, vertices[i + 1]);
        minZ = Math.min(minZ, vertices[i + 2]);
        maxX = Math.max(maxX, vertices[i]);
        maxY = Math.max(maxY, vertices[i + 1]);
        maxZ = Math.max(maxZ, vertices[i + 2]);
      }
      
      // Store binary data
      binaryDataChunks.push(vertices);
      if (normals) {
        binaryDataChunks.push(normals);
      }
      binaryDataChunks.push(indices);
      
      // Create accessors and buffer views for this mesh
      const positionAccessorIndex = accessors.length;
      accessors.push({
        bufferView: bufferViews.length,
        componentType: 5126, // FLOAT
        count: vertices.length / 3,
        type: "VEC3",
        min: [minX, minY, minZ],
        max: [maxX, maxY, maxZ]
      });
      
      bufferViews.push({
        buffer: 0,
        byteOffset: bufferOffset,
        byteLength: vertices.byteLength,
        target: 34962 // ARRAY_BUFFER
      });
      bufferOffset += vertices.byteLength;
      
      const normalAccessorIndex = normals ? accessors.length : null;
      if (normals) {
        accessors.push({
          bufferView: bufferViews.length,
          componentType: 5126, // FLOAT
          count: normals.length / 3,
          type: "VEC3"
        });
        
        bufferViews.push({
          buffer: 0,
          byteOffset: bufferOffset,
          byteLength: normals.byteLength,
          target: 34962 // ARRAY_BUFFER
        });
        bufferOffset += normals.byteLength;
      }
      
      const indicesAccessorIndex = accessors.length;
      accessors.push({
        bufferView: bufferViews.length,
        componentType: 5125, // UNSIGNED_INT
        count: indices.length,
        type: "SCALAR"
      });
      
      bufferViews.push({
        buffer: 0,
        byteOffset: bufferOffset,
        byteLength: indices.byteLength,
        target: 34963 // ELEMENT_ARRAY_BUFFER
      });
      bufferOffset += indices.byteLength;
      
      // Create material for this organ
      const materialIndex = materials.length;
      const colorRgba = hexToRgb(organData.color || '#ef4444');
      materials.push({
        pbrMetallicRoughness: {
          baseColorFactor: colorRgba,
          metallicFactor: 0.1,
          roughnessFactor: 0.3
        },
        name: organData.name || `material_${meshIndex}`,
        doubleSided: true
      });
      
      // Create mesh for this organ
      const meshObject = {
        primitives: [{
          attributes: {
            POSITION: positionAccessorIndex,
            ...(normalAccessorIndex !== null ? { NORMAL: normalAccessorIndex } : {})
          },
          indices: indicesAccessorIndex,
          material: materialIndex,
          mode: 4 // TRIANGLES
        }],
        name: organData.name || `mesh_${meshIndex}`
      };
      
      meshes.push(meshObject);
      
      // Create node for this mesh
      nodes.push({
        mesh: meshIndex,
        name: organData.name || `node_${meshIndex}`
      });
    });
    
    // Create glTF JSON structure with all meshes, materials, and nodes
    const gltf = {
      asset: { version: "2.0", generator: "Snorkle Organ Segmentation" },
      scene: 0,
      scenes: [{ nodes: nodes.map((_, idx) => idx) }], // Reference all nodes
      nodes: nodes,
      meshes: meshes,
      materials: materials,
      accessors: accessors,
      bufferViews: bufferViews,
      buffers: [{
        byteLength: bufferOffset
      }]
    };
    
    // Convert JSON to binary
    const jsonString = JSON.stringify(gltf);
    const jsonBuffer = new TextEncoder().encode(jsonString);
    const jsonPadding = (4 - (jsonBuffer.length % 4)) % 4;
    const jsonChunkLength = jsonBuffer.length + jsonPadding;
    
    // Combine all binary data chunks into a single buffer
    const totalBinaryLength = binaryDataChunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const binaryData = new Uint8Array(totalBinaryLength);
    let offset = 0;
    for (const chunk of binaryDataChunks) {
      binaryData.set(new Uint8Array(chunk.buffer), offset);
      offset += chunk.byteLength;
    }
    
    const binaryPadding = (4 - (binaryData.length % 4)) % 4;
    const binaryChunkLength = binaryData.length + binaryPadding;
    
    // Create GLB file
    const glbLength = 12 + 8 + jsonChunkLength + 8 + binaryChunkLength;
    const glb = new ArrayBuffer(glbLength);
    const view = new DataView(glb);
    
    // GLB header
    view.setUint32(0, 0x46546C67, true); // magic "glTF"
    view.setUint32(4, 2, true); // version
    view.setUint32(8, glbLength, true); // length
    
    // JSON chunk
    view.setUint32(12, jsonChunkLength, true);
    view.setUint32(16, 0x4E4F534A, true); // "JSON"
    const glbArray = new Uint8Array(glb);
    glbArray.set(jsonBuffer, 20);
    for (let i = 0; i < jsonPadding; i++) {
      glbArray[20 + jsonBuffer.length + i] = 0x20; // space padding
    }
    
    // Binary chunk
    const binaryChunkOffset = 20 + jsonChunkLength;
    view.setUint32(binaryChunkOffset, binaryChunkLength, true);
    view.setUint32(binaryChunkOffset + 4, 0x004E4942, true); // "BIN\0"
    glbArray.set(binaryData, binaryChunkOffset + 8);
    for (let i = 0; i < binaryPadding; i++) {
      glbArray[binaryChunkOffset + 8 + binaryData.length + i] = 0; // zero padding
    }
    
    // Return blob
    return new Blob([glb], { type: 'model/gltf-binary' });
  };

  const handleShareClick = async () => {
    if (isDicomFile || !meshData) return;
    
    setUploadingShare(true);
    setError(null);
    
    try {
      // Collect all visible organs with their colors
      const organsToShare = [];
      
      if (meshData.multi_organ) {
        meshData.organs.forEach(organData => {
          if (visibleOrgans[organData.name]) {
            organsToShare.push({
              name: organData.name,
              vertices: organData.vertices,
              faces: organData.faces,
              normals: organData.normals,
              color: organData.color // Include color from mesh data
            });
          }
        });
      } else {
        // Single organ
        organsToShare.push({
          name: organ,
          vertices: meshData.vertices,
          faces: meshData.faces,
          normals: meshData.normals,
          color: '#ef4444' // Default red color for single organ
        });
      }
      
      if (organsToShare.length === 0) {
        setError('No organs selected to share');
        setUploadingShare(false);
        return;
      }
      
      
      // Create a single GLB file containing all visible organs
      const glbBlob = createGLBBlob(organsToShare, 'organ_segmentation_scene');
      
      // Convert blob to File object
      const glbFile = new File(
        [glbBlob], 
        'organ_segmentation_scene.glb', 
        { type: 'model/gltf-binary' }
      );
      
      
      // Upload to Snorkle
      APIFileVisualization(
        glbFile,
        'Organ Segmentation Scene',
        `3D visualization with ${organsToShare.length} organ${organsToShare.length > 1 ? 's' : ''}`,
        (response) => {
          setCreatedVisualization(response);
          setUploadingShare(false);
          
          // Update visCache
          if (response.visualization) {
            setVisCache(prev => ({
              ...prev,
              [response.visualization.id]: response.visualization
            }));
          }
          
          // Show share modal
          setShowShare(true);
        },
        (error) => {
          setError(`Failed to create visualization: ${error.message || error}`);
          setUploadingShare(false);
        },
        false, // isOrg
        {} // no additional args
      );
      
    } catch (err) {
      setError(`Failed to prepare visualization: ${err.message}`);
      setUploadingShare(false);
    }
  };

  if (loading) {
    return (
      <div className="mesh-viewer-empty">
        <div className="spinner"></div>
        <p>Loading 3D mesh...</p>
      </div>
    );
  }

  if (error && !meshData) {
    return (
      <div className="mesh-viewer-empty error">
        <p>⚠️ {error}</p>
        <button onClick={loadMesh} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!meshData) {
    return <div className="mesh-viewer-empty">No mesh data available</div>;
  }

  return (
    <div className="mesh-viewer">
      {/* Upload loading overlay */}
      {uploadingShare && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 17, 23, 0.95)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          gap: '20px'
        }}>
          <div className="thinking-loader" style={{
            width: '48px',
            height: '48px'
          }}></div>
          <div style={{
            color: '#e5e9f5',
            fontSize: '16px',
            fontWeight: 600,
            textAlign: 'center',
            maxWidth: '300px'
          }}>
            <div style={{ marginBottom: '8px' }}>Creating Visualization...</div>
            <div style={{ fontSize: '14px', opacity: 0.7, fontWeight: 400 }}>
              Uploading scene to Snorkle
            </div>
          </div>
        </div>
      )}
      
      {/* Error display for upload failures */}
      {error && meshData && !uploadingShare && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(239, 68, 68, 0.9)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          zIndex: 1000,
          maxWidth: '80%',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>Upload Failed</div>
          <div style={{ fontSize: '14px' }}>{error}</div>
          <button 
            onClick={() => setError(null)}
            style={{
              marginTop: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Floating control buttons */}
      <div className="mesh-floating-controls">
        {/* View toggle button - only show if onToggleLayout is provided */}
        {onToggleLayout && (
          <button
            className="control-btn view-toggle-btn"
            onClick={onToggleLayout}
            title={viewerLayout === 'split' ? 'Show 3D View Only' : 'Show Both Viewers'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {viewerLayout === 'split' ? (
                <rect x="3" y="3" width="18" height="18" rx="2"/>
              ) : (
                <>
                  <rect x="3" y="3" width="7" height="18" rx="2"/>
                  <rect x="14" y="3" width="7" height="18" rx="2"/>
                </>
              )}
            </svg>
            <span className="control-btn-label">
              {viewerLayout === 'split' ? '3D' : 'Split'}
            </span>
          </button>
        )}
        {meshData?.multi_organ && (
          <button
            className={`control-btn ${showOrgans ? 'active' : ''}`}
            onClick={() => setShowOrgans(!showOrgans)}
            title="Toggle organ visibility"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </button>
        )}
        <button
          className={`control-btn ${showDownload ? 'active' : ''}`}
          onClick={() => setShowDownload(!showDownload)}
          title="Download mesh"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        <button
          className={`control-btn ${isDicomFile || uploadingShare ? 'disabled' : ''}`}
          onClick={handleShareClick}
          title={isDicomFile ? "Sharing disabled for DICOM data" : uploadingShare ? "Creating visualization..." : "Share visualization"}
          disabled={isDicomFile || uploadingShare}
        >
          {uploadingShare ? (
            <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></div>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          )}
        </button>
      </div>

      {/* Collapsible panels */}
      {showOrgans && meshData?.multi_organ && (
        <div className="mesh-panel organ-panel">
          <div className="panel-header">
            <h4>ORGANS ({meshData.organ_count})</h4>
            <button className="panel-close" onClick={() => setShowOrgans(false)}>×</button>
          </div>
          <div className="panel-content">
            {meshData.organs.map(organ => (
              <label key={organ.name} className="organ-item">
                <input
                  type="checkbox"
                  checked={visibleOrgans[organ.name] || false}
                  onChange={() => toggleOrganVisibility(organ.name)}
                />
                <span className="organ-color" style={{ backgroundColor: organ.color }}></span>
                <span className="organ-label">{organ.name.replace(/_/g, ' ')}</span>
              </label>
            ))}
          </div>
        </div>
      )}


      {showDownload && (
        <div className="mesh-panel download-panel">
          <div className="panel-header">
            <h4>DOWNLOAD</h4>
            <button className="panel-close" onClick={() => setShowDownload(false)}>×</button>
          </div>
          <div className="panel-content">
            <button onClick={() => handleDownload('obj')} className="download-option">
              <span className="format-label">OBJ</span>
              <span className="format-desc">Blender, Maya</span>
            </button>
            <button onClick={() => handleDownload('stl')} className="download-option">
              <span className="format-label">STL</span>
              <span className="format-desc">3D Printing</span>
            </button>
            <button onClick={() => handleDownload('glb')} className="download-option">
              <span className="format-label">GLB</span>
              <span className="format-desc">Web, AR/VR</span>
            </button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        show={showShare && !isDicomFile && createdVisualization}
        onHide={() => {
          setShowShare(false);
          setCreatedVisualization(null);
        }}
        url={createdVisualization?.visualization_url || createdVisualization?.url || window.location.href}
        canEmail={true}
        isPresentation={false}
        visual={createdVisualization?.visualization || null}
      />

      <div 
        className="canvas-container" 
        onClick={() => setAutoRotate(false)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMousePosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          });
        }}
      >
        <Canvas shadows>
          <PerspectiveCamera makeDefault position={[0, 3, 0]} fov={50} rotation={[Math.PI / 2, 0, 0]} />
          <ambientLight intensity={0.3} />
          <directionalLight
            position={[10, 10, 5]}
            intensity={0.6}
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
          />
          <pointLight position={[-10, -10, -5]} intensity={0.3} />
          {meshData.multi_organ ? (
            meshData.organs.map(organData => (
              visibleOrgans[organData.name] && (
                <OrganMesh
                  key={organData.name}
                  name={organData.name}
                  vertices={organData.vertices}
                  faces={organData.faces}
                  normals={organData.normals}
                  color={organData.color}
                  opacity={1.0}
                  onHover={setHoveredOrgan}
                  onHoverEnd={() => setHoveredOrgan(null)}
                  isHovered={hoveredOrgan === organData.name}
                />
              )
            ))
          ) : (
            <OrganMesh
              name={organ}
              vertices={meshData.vertices}
              faces={meshData.faces}
              normals={meshData.normals}
              color="#ef4444"
              opacity={1.0}
              onHover={setHoveredOrgan}
              onHoverEnd={() => setHoveredOrgan(null)}
              isHovered={hoveredOrgan === organ}
            />
          )}
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            autoRotate={autoRotate}
            autoRotateSpeed={1.5}
            onChange={() => setAutoRotate(false)}
          />
          <Environment preset="studio" />
          <gridHelper args={[10, 10]} />
        </Canvas>
      </div>
      
      {/* Hover label */}
      {hoveredOrgan && (
        <div 
          className="organ-hover-label"
          style={{
            left: `${mousePosition.x + 20}px`,
            top: `${mousePosition.y + 20}px`
          }}
        >
          {hoveredOrgan.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </div>
      )}
    </div>
  );
}

export default MeshViewer;

